import type { TaskTracking } from '@mimir/common/async/taskTracking';
import { formatHmsmTime } from '@mimir/common/util/duration';
import { makeError } from '@mimir/common/util/exception';
import type { Fraction } from '@mimir/common/util/fractionType';
import { approximateFractionValue, multiplyFractionBy } from '@mimir/common/util/integerMath';
import { invariant } from '@mimir/common/util/invariant';
import type { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import minBy from 'lodash/minBy';
import type { BufferingContext } from './fetching';
import type { BufferCoordinator } from './sourceBufferCoordinator';
import type { StreamCandidate } from './streamCandidate';
import type { BufferingResult, NextTask } from './tasks';
import { type AudioTransform, getAudioTransformId } from './audioTransform';
import type { FragmentedMp4Buffers } from '@mimir/common/mp4/fragmentedMp4Types';

export interface BufferingParameters {
  prebufferDuration: number;
  segmentCount: number;
  streaming: boolean
}

export class BufferTaskSearch {
  constructor(private context: BufferingContext, private ongoingBufferingTasks: TaskTracking<BufferingResult>, private segmentDuration: Fraction, private audioTransform: AudioTransform) {
  }

  private makeNextBufferTask(streamId: StreamRoleId, segmentTime: number, taskId: string, coordinator: BufferCoordinator, isFinalSegment: boolean): NextTask {
    const initKey = this.context.fragmentedMp4Provider.itemId + ':' + streamId;
    let initBuf: BufferSource | null = null;
    if (coordinator.currentInitialization?.key == initKey) {
      initBuf = coordinator.currentInitialization.buffer;
    }

    const finalTaskId = taskId;

    const hotStart = Math.max(
      0,
      this.context.currentTime - approximateFractionValue(this.segmentDuration) * 1.5
    );
    const hotEnd = this.context.currentTime + 30.0;

    // Reuse current init buffer if it is available and matches
    const finalInitKey = initKey;
    const finalCoordinator = coordinator;
    const finalStreamId = streamId;

    return {
      id: finalTaskId,
      task: () => {
        return this.ongoingBufferingTasks.runOrPiggyback(finalTaskId, async () => {
          let bufs: FragmentedMp4Buffers | undefined;
          try {
            const needInitialization = initBuf == null;
            bufs = await this.context.fragmentedMp4Provider.getMediaSegmentBuffers(needInitialization, finalStreamId, segmentTime, this.audioTransform);

            let indexedBuf: BufferSource | null = null;
            if (needInitialization) {
              initBuf = bufs.initialization;
            }
            indexedBuf = bufs.mediaSegment;
            invariant(initBuf, 'Expected initBuf');
            invariant(indexedBuf, 'Expected indexedBuf');

            await finalCoordinator.bufferMediaSegment(
              finalInitKey,
              initBuf,
              indexedBuf,
              isFinalSegment,
              bufs.info,
              hotStart,
              hotEnd,
              taskId
            );
            const result: BufferingResult = {
              streamId: finalStreamId,
              segmentTime,
              buffers: bufs
            };
            return result;
          } catch (ex) {
            throw makeError(
              'Buffering ' +
                this.context.fragmentedMp4Provider.itemId +
                ' stream ' +
                streamId +
                ' segment ' +
                segmentTime +
                ' failed',
              'SegmentError',
              { cause: ex, info: bufs?.info }
            );
          }
        });
      }
    };
  }

  private checkNext(segmentTime: number, prebufferTarget: number, params: BufferingParameters, streamCandidates: StreamCandidate[]): { nextTask: NextTask | null, segmentTime: number } {
    // Avoid fetching segments too far ahead of the current time.
    if (approximateFractionValue(multiplyFractionBy(this.segmentDuration, segmentTime)) > prebufferTarget) {
      const debugInfo = streamCandidates.map(c => c.streamInfo.streamId + ':' + c.unbufferedSegmentIterator.toString()).join(', ');
      return { nextTask: { blocker: 'Segment ' + segmentTime + ' is more than ' + formatHmsmTime(params.prebufferDuration) + ' ahead of ' + formatHmsmTime(this.context.currentTime) + ', ' + debugInfo }, segmentTime: segmentTime };
    }

    const isFinalSegment = segmentTime + 1 == params.segmentCount && !params.streaming;

    // Avoid fetching segments not yet available.
    if (segmentTime >= params.segmentCount) {
      if (params.streaming) {
        return { nextTask: { blocker: 'Segment ' + segmentTime + ' is not available yet' }, segmentTime };
      } else {
        return { nextTask: { blocker: 'Has reached end of media ' + JSON.stringify({ segmentTime, segmentCount: params.segmentCount }) }, segmentTime };
      }
    }

    /*
      For the final segment, we can't rely on the buffered ranges to determine
      whether it has been buffered or not, since the segment will only be a
      partial segment duration. Instead disqualify buffers been marked ended.
    */
    if (isFinalSegment) {
      streamCandidates = streamCandidates.filter(sc => !sc.streamInfo.coordinator.hasEnded);
      if (streamCandidates.length == 0) {
        return { nextTask: { blocker: 'Has reached end of stream ' + JSON.stringify({ segmentTime, segmentCount: params.segmentCount }) }, segmentTime };
      }
    }

    const start = segmentTime;
    const streamUnbuffered = streamCandidates.map(sc => ({ nextUnbuffered: sc.unbufferedSegmentIterator.nextUnbufferedSegment(start), streamInfo: sc.streamInfo, buffered: sc.unbufferedSegmentIterator.buffered }));
    const earliestUnbuffered = minBy(streamUnbuffered, su => su.nextUnbuffered);

    if (!earliestUnbuffered) {
      return { nextTask: { blocker: 'No buffers' }, segmentTime: segmentTime };
    }

    if (start != earliestUnbuffered.nextUnbuffered) {
      return { nextTask: null, segmentTime: earliestUnbuffered.nextUnbuffered };
    }

    const streamInfo = earliestUnbuffered.streamInfo;

    /*
      Avoid re-fetching segments that are already in the process of being
      buffered.
    */
    const streamId = streamInfo.streamId;
    const taskId = streamId + ':' + segmentTime + ':' + getAudioTransformId(this.audioTransform);
    if (this.ongoingBufferingTasks.isOngoing(taskId)) {
      return { nextTask: null, segmentTime: segmentTime + 1 };
    }

    const coordinator = streamInfo.coordinator;
    invariant(coordinator != null, 'Expect videoBuffer');

    if (coordinator.lastQueued == taskId) {
      /*
        This is the same segment we last added to the source buffer. Either
        we've reached the end of the stream, or the expected segment content range
        is not reflected in the actual buffered range.
      */
      const expectedStart = approximateFractionValue(multiplyFractionBy(this.segmentDuration, segmentTime));
      const expectedEnd = approximateFractionValue(multiplyFractionBy(this.segmentDuration, segmentTime + 1));
      if (isFinalSegment && !coordinator.hasEnded) {
        console.log('May need to resignal end of stream', { isFinalSegment, streamId, hasEnded: coordinator.hasEnded });
      }
      return { nextTask: { blocker: 'Segment ' + taskId + ' for range ' + expectedStart + '-' + expectedEnd + ' already buffered but not in ' + timeRangesToString(earliestUnbuffered.buffered) }, segmentTime: segmentTime };
    }

    return { nextTask: this.makeNextBufferTask(streamId, segmentTime, taskId, coordinator, isFinalSegment), segmentTime: segmentTime };
  }

  findNextBufferingTask(streamCandidates: StreamCandidate[], params: BufferingParameters): NextTask {
    if (this.segmentDuration.numerator === 0) {
      return { blocker: 'Segment duration not available' };
    }

    /*
      the video player sometimes fails to start playing because according to its calculation.
      It believes that it doesn't need to download the video element containing the desired PTS (presentation timestamp).
      Since it thinks its the next segment that contains the PTS.
      Hence, deducting 10% of the segment duration from the current time.
    */
    const currentTime = this.context.currentTime - approximateFractionValue(multiplyFractionBy(this.segmentDuration, 0.10));

    // Figure out which segment index if any to fetch and buffer.
    let segmentTime = Math.floor(
      (currentTime * this.segmentDuration.denominator) / this.segmentDuration.numerator
    );

    segmentTime = Math.max(0, segmentTime);

    const prebufferTarget = params.prebufferDuration + this.context.currentTime;

    while (true) {
      const next = this.checkNext(segmentTime, prebufferTarget, params, streamCandidates);
      if (next.nextTask) {
        return next.nextTask;
      }

      segmentTime = next.segmentTime;
    }
  }
}

function timeRangesToString(ranges: TimeRanges) {
  return [...Array(ranges.length)].map((_, i) => ranges.start(i) + '-' + ranges.end(i)).join(', ');
}
